import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ConsumerSidebarComponent } from '../sidebar/consumer-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AuthApiService, FileApiService } from '@angular-workspace/services';
import { ModalComponent } from '../../shared/components/primeng/modal/modal.component';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ToasterService } from '../../services/toaster.service';

interface EntityType {
  label: string;
  value: string;
}

interface EntitySector {
  label: string;
  value: string;
}

@Component({
  selector: 'app-consumer-create-organisation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    ConsumerSidebarComponent,
    PageHeaderComponent,
    ModalComponent,
    PdfViewerModule,
    ConfirmationDialogComponent,
  ],
  templateUrl: './create-organisation.component.html',
  styleUrl: './create-organisation.component.scss',
})
export class ConsumerCreateOrganisationComponent implements OnInit, OnDestroy {
  @ViewChild('sidebar') sidebarComponent!: ConsumerSidebarComponent;
  
  userDetailsForm!: FormGroup;
  entityDetailsForm!: FormGroup;
  documentsForm!: FormGroup;
  
  activeStep: number = 0;
  
  // Dropdown state
  isEntityTypeDropdownOpen = false;
  isEntitySectorDropdownOpen = false;
  
  // Organisation name validation
  organisationNames: string[] = [];
  checkingName = false;
  nameExists: boolean | null = null;
  private entityNameSubject = new Subject<string>();
  private entityNameSubscription?: Subscription;
  
  // Dropdown options
  entityTypes: EntityType[] = [
    { label: 'Private Limited Company', value: 'private_limited' },
    { label: 'Public Limited Company', value: 'public_limited' },
    { label: 'Partnership Firm', value: 'partnership' },
    { label: 'Sole Proprietorship', value: 'sole_proprietorship' },
    { label: 'Limited Liability Partnership', value: 'llp' },
    { label: 'Government Organization', value: 'government' },
    { label: 'Non-Profit Organization', value: 'non_profit' },
  ];
  
  entitySectors: EntitySector[] = [
    { label: 'Agriculture', value: 'agriculture' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Education', value: 'education' },
    { label: 'Finance', value: 'finance' },
    { label: 'Technology', value: 'technology' },
    { label: 'Manufacturing', value: 'manufacturing' },
    { label: 'Retail', value: 'retail' },
    { label: 'Transportation', value: 'transportation' },
    { label: 'Energy', value: 'energy' },
    { label: 'Real Estate', value: 'real_estate' },
    { label: 'Others', value: 'others' },
  ];
  
  // File upload tracking
  uploadedFiles: { [key: string]: File | null } = {
    incorporation: null,
    panCard: null,
    authorization: null,
    others: null,
  };

  // File upload status tracking
  uploadStatus: { [key: string]: { loading: boolean; success: boolean; error: string | null; assetId?: string; key?: string; downloadUrl?: string; loadingPreview?: boolean } } = {
    incorporation: { loading: false, success: false, error: null },
    panCard: { loading: false, success: false, error: null },
    authorization: { loading: false, success: false, error: null },
    others: { loading: false, success: false, error: null },
  };

  // PDF Preview Modal
  showPdfPreviewModal: boolean = false;
  currentPdfUrl: string | Uint8Array = '';
  currentPdfTitle: string = '';
  pdfLoading: boolean = false;
  showSuccessModal = false;
  // Cache blob URLs to avoid refetching
  private blobUrlCache: { [key: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authApiService: AuthApiService,
    private fileApiService: FileApiService,
    private elementRef: ElementRef,
    private http: HttpClient,
    private toasterService: ToasterService
  ) {}

  async ngOnInit(): Promise<void> {
    this.initForms();
    await this.loadUserInfo();
    this.setupEntityNameValidation();
    await this.loadAllOrganisations();
  }
  
  ngOnDestroy(): void {
    if (this.entityNameSubscription) {
      this.entityNameSubscription.unsubscribe();
    }
  }
  
  setupEntityNameValidation(): void {
    // Set up debounced subscription for entity name changes
    this.entityNameSubscription = this.entityNameSubject
      .pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged() // Only trigger if value actually changed
      )
      .subscribe(orgName => {
        this.checkOrganisationNameExists(orgName);
      });
    
    // Subscribe to entityName form control value changes
    const entityNameControl = this.entityDetailsForm.get('entityName');
    if (entityNameControl) {
      entityNameControl.valueChanges.subscribe(value => {
        const trimmedValue = (value || '').trim();
        if (trimmedValue.length >= 3) {
          this.entityNameSubject.next(trimmedValue);
        } else {
          this.nameExists = null;
          this.checkingName = false;
        }
      });
    }
  }
  
  async loadAllOrganisations(): Promise<void> {
    try {
      const response = await this.authApiService.getAllOrganisations(1, 100);
      
      // Extract orgName from response
      let organisations: any[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        organisations = response['result'];
      } else if (Array.isArray(response)) {
        organisations = response;
      }
      
      // Extract unique organisation names (case-insensitive comparison)
      this.organisationNames = organisations
        .map(org => org.orgName || org.name || org.organisationName)
        .filter((name): name is string => name != null && name !== '')
        .map(name => name.toLowerCase().trim());
    } catch (error) {
      console.error('Error loading organisations:', error);
      this.organisationNames = [];
    }
  }
  
  checkOrganisationNameExists(orgName: string): void {
    if (!orgName || orgName.length < 3) {
      this.nameExists = null;
      this.checkingName = false;
      return;
    }
    
    this.checkingName = true;
    const normalizedName = orgName.toLowerCase().trim();
    this.nameExists = this.organisationNames.some(name => name === normalizedName);
    this.checkingName = false;
  }
  
  async loadUserInfo(): Promise<void> {
    try {
      const userInfo = await this.authApiService.getUserInfo();
      
      // The API response may have a 'result' object containing the actual user data
      const result = userInfo['result'] || userInfo;
      
      console.log('User info API response:', result); // Debug log
      
      // Extract firstName, lastName, and email from response
      // Based on profile component mapping: firstName -> familyName, lastName -> givenName
      let firstName = result['givenName'] || result['firstName'] || result['first_name'] || '';
      let lastName = result['familyName'] || result['lastName'] || result['last_name'] || '';
      
      // If name is provided as a single field, try to split it
      if (!firstName && !lastName && result['name']) {
        const nameParts = result['name'].trim().split(/\s+/);
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        } else if (nameParts.length === 1) {
          firstName = nameParts[0];
        }
      }
      
      // Also check userName field
      if (!firstName && !lastName && result['userName']) {
        const nameParts = result['userName'].trim().split(/\s+/);
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        } else if (nameParts.length === 1) {
          firstName = nameParts[0];
        }
      }
      
      const userEmail = result['email'] || result['emailAddress'] || result['registeredEmail'] || result['emailId'] || '';
      
      console.log('Extracted user data:', { firstName, lastName, userEmail }); // Debug log
      
      // Prepopulate the form fields (even if disabled, patchValue will set the value)
      this.userDetailsForm.patchValue({ 
        firstName: firstName || '',
        lastName: lastName || '',
        registeredEmail: userEmail || ''
      });
    } catch (error) {
      console.error('Error loading user info:', error);
      // Keep empty values if API fails (fields remain disabled)
    }
  }
  
  initForms(): void {
    // Step 1: User Details
    this.userDetailsForm = this.fb.group({
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      registeredEmail: [{ value: '', disabled: true }],
      jobTitle: ['', [Validators.required]],
      employeeId: ['', [Validators.required]],
    });

    // Step 2: Entity Details
    this.entityDetailsForm = this.fb.group({
      entityType: ['', [Validators.required]],
      entitySector: ['', [Validators.required]],
      entityName: ['', [Validators.required, Validators.minLength(3)]],
      websiteUrl: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]],
      address: ['', [Validators.required, Validators.minLength(10)]],
    });

    // Step 3: Documents
    this.documentsForm = this.fb.group({
      incorporation: ['', [Validators.required]],
      panCard: ['', [Validators.required]],
      authorization: ['', [Validators.required]],
      others: [''],
    });
  }

  isFieldInvalid(form: FormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getErrorMessage(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (field?.hasError('required')) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }
    if (field?.hasError('minlength')) {
      return `${this.getFieldLabel(fieldName)} is too short`;
    }
    if (field?.hasError('pattern')) {
      return 'Please enter a valid URL (starting with http:// or https://)';
    }
    return '';
  }

  getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      firstName: 'First name',
      lastName: 'Last name',
      jobTitle: 'Job title',
      employeeId: 'Employee ID',
      entityType: 'Entity type',
      entitySector: 'Entity sector',
      entityName: 'Entity name',
      websiteUrl: 'Website URL',
      address: 'Address',
      incorporation: 'Incorporation certificate',
      panCard: 'PAN card',
      authorization: 'Letter of authorization',
    };
    return labels[fieldName] || fieldName;
  }

  async onFileSelect(event: Event, documentType: string): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.uploadedFiles[documentType] = file;
      
      // Reset upload status
      this.uploadStatus[documentType] = {
        loading: true,
        success: false,
        error: null,
        assetId: undefined,
        key: undefined,
        downloadUrl: undefined,
        loadingPreview: false,
      };
      
      this.documentsForm.patchValue({
        [documentType]: file.name,
      });
      this.documentsForm.get(documentType)?.markAsTouched();

      // Upload the file using assetUpload
      try {
        const response = await this.fileApiService.assetUpload(file);

        // Treat any 2xx as success (backend may return 200 or 201).
        const status = response?.status;
        const isSuccess =
          typeof status === 'number'
            ? status >= 200 && status < 300
            : Boolean(response?.data?.success ?? response?.data?.status ?? false);
        
        // Store the asset ID and key from response if available
        const responseData = response.data || response;
        const assetId = responseData?.id || responseData?.assetId || responseData?.result?.id || responseData?.result?.assetId;
        
        // Extract key from response - check multiple possible locations
        const key = responseData?.key || 
                   responseData?.result?.key || 
                   responseData?.data?.key ||
                   responseData?.id || // Fallback to id if key is not available
                   assetId;
        
        this.uploadStatus[documentType] = {
          loading: false,
          success: isSuccess,
          error: null,
          assetId: assetId,
          key: key, // Store key for later use when preview is clicked
        };

        // Cache a blob URL from the file we just uploaded so preview opens instantly
        // without calling the download API and re-fetching the PDF
        if (isSuccess && file) {
          if (this.blobUrlCache[documentType]) {
            URL.revokeObjectURL(this.blobUrlCache[documentType]);
          }
          this.blobUrlCache[documentType] = URL.createObjectURL(file);
        }
        
        console.log(`File uploaded successfully for ${documentType}:`, response);
      } catch (error: any) {
        console.error(`Error uploading file for ${documentType}:`, error);
        this.uploadStatus[documentType] = {
          loading: false,
          success: false,
          error: error?.error?.detail || error?.message || 'Failed to upload file',
        };
      }
    }
  }

  removeFile(documentType: string): void {
    // Revoke cached blob URL if it exists
    if (this.blobUrlCache[documentType]) {
      URL.revokeObjectURL(this.blobUrlCache[documentType]);
      delete this.blobUrlCache[documentType];
    }
    
    this.uploadedFiles[documentType] = null;
    this.uploadStatus[documentType] = {
      loading: false,
      success: false,
      error: null,
      assetId: undefined,
      key: undefined,
      downloadUrl: undefined,
      loadingPreview: false,
    };
    this.documentsForm.patchValue({
      [documentType]: '',
    });
    
    // Reset the file input element so the same file can be selected again
    const fileInput = document.getElementById(documentType) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  async openPdfPreview(documentType: string): Promise<void> {
    const uploadStatus = this.uploadStatus[documentType];
    
    // Prefer cached blob URL (set right after upload) for instant loading
    if (this.blobUrlCache[documentType]) {
      this.currentPdfUrl = this.blobUrlCache[documentType];
      this.currentPdfTitle = this.getDocumentLabel(documentType);
      this.showPdfPreviewModal = true;
      return;
    }

    // If we still have the file in memory (e.g. just uploaded), use it without calling the API
    const localFile = this.uploadedFiles[documentType];
    if (localFile) {
      const blobUrl = URL.createObjectURL(localFile);
      this.blobUrlCache[documentType] = blobUrl;
      this.currentPdfUrl = blobUrl;
      this.currentPdfTitle = this.getDocumentLabel(documentType);
      this.showPdfPreviewModal = true;
      return;
    }
    
    // No local file or cache: call download API (e.g. user returned to the step later)
    const key = uploadStatus?.key;
    if (!key) {
      console.error(`No key available for ${documentType}`);
      this.toasterService.error(
        `Unable to preview ${this.getDocumentLabel(documentType)}. Please re-upload the file.`,
        3000
      );
      return;
    }
    
    // Set loading state
    this.uploadStatus[documentType].loadingPreview = true;
    this.pdfLoading = true;
    
    try {
      // Call downloadAssets with the payload
      const downloadPayload = {
        key: key,
        expiresIn: 0
      };
      
      const downloadResponse = await this.fileApiService.downloadAssets(downloadPayload);
      console.log(`Download assets called successfully for ${documentType}:`, downloadResponse);
      
      // Extract the download URL from the response
      // Based on the image, the response structure is: { success: true, data: { url: "...", ... } }
      const downloadUrl = downloadResponse?.data?.url || 
                         downloadResponse?.url || 
                         downloadResponse?.data?.downloadUrl ||
                         downloadResponse?.result?.url ||
                         downloadResponse?.result?.downloadUrl;
      
      if (downloadUrl) {
        // Store the download URL for reference
        this.uploadStatus[documentType].downloadUrl = downloadUrl;
        
        // Fetch PDF as blob to avoid X-Frame-Options issue
        try {
          const pdfBlob = await this.fetchPdfAsBlob(downloadUrl);
          // Create blob URL from the blob
          const blobUrl = URL.createObjectURL(pdfBlob);
          
          // Cache the blob URL for this document type
          this.blobUrlCache[documentType] = blobUrl;
          
          this.currentPdfUrl = blobUrl;
          this.currentPdfTitle = this.getDocumentLabel(documentType);
          this.showPdfPreviewModal = true;
        } catch (fetchError: any) {
          console.error(`Error fetching PDF as blob for ${documentType}:`, fetchError);
          // Fallback: try using the URL directly (might not work due to X-Frame-Options)
          this.currentPdfUrl = downloadUrl;
          this.currentPdfTitle = this.getDocumentLabel(documentType);
          this.showPdfPreviewModal = true;
        }
      } else {
        console.error(`No download URL found in response for ${documentType}`);
        this.toasterService.error(
          `Unable to preview ${this.getDocumentLabel(documentType)}. Please re-upload the file.`,
          3000
        );
      }
    } catch (downloadError: any) {
      console.error(`Error calling downloadAssets for ${documentType}:`, downloadError);
      this.toasterService.error(
        `Failed to load ${this.getDocumentLabel(documentType)} preview. Please try again.`,
        3000
      );
    } finally {
      this.uploadStatus[documentType].loadingPreview = false;
      this.pdfLoading = false;
    }
  }

  closePdfPreview(): void {
    this.showPdfPreviewModal = false;
    // Don't revoke the blob URL here - keep it cached for faster reopening
    // It will be revoked when the file is removed
    this.currentPdfUrl = '';
    this.currentPdfTitle = '';
    this.pdfLoading = false;
  }

  /**
   * Fetches a PDF from a URL and returns it as a Blob
   * Uses Fetch API directly to avoid interceptor issues with signed URLs
   */
  private async fetchPdfAsBlob(url: string): Promise<Blob> {
    try {
      // Use Fetch API directly to bypass Angular interceptors
      // This is necessary because signed S3 URLs are sensitive to headers
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Verify it's actually a PDF
      if (blob.type === 'application/pdf' || blob.type === 'application/octet-stream' || blob.size > 0) {
        return blob;
      } else {
        throw new Error('Response is not a valid PDF');
      }
    } catch (error) {
      console.error('Error fetching PDF:', error);
      throw error;
    }
  }

  getDocumentLabel(documentType: string): string {
    const labels: { [key: string]: string } = {
      incorporation: 'Firm Incorporation Certificate',
      panCard: 'PAN Card',
      authorization: 'Letter of Authorization',
      others: 'Other Documents',
    };
    return labels[documentType] || 'Document';
  }

  getFileName(documentType: string): string {
    return this.uploadedFiles[documentType]?.name || '';
  }

  async onSubmit(): Promise<void> {
    // Mark all forms as touched to show validation errors
    this.markAllFormsAsTouched();
    
    // Check if all forms are valid
    if (!this.userDetailsForm.valid || !this.entityDetailsForm.valid || !this.documentsForm.valid) {
      console.error('Form validation failed');
      this.toasterService.error('Please fill all required fields and upload the required documents.', 3000);
      return;
    }

    // Check if all required documents are uploaded successfully
    const requiredDocs = ['incorporation', 'panCard', 'authorization'];
    const missingDocs = requiredDocs.filter(doc => !this.uploadStatus[doc].success);
    
    if (missingDocs.length > 0) {
      console.error('Some required documents are not uploaded:', missingDocs);
      this.toasterService.error(
        `Please upload/complete these documents before submitting: ${missingDocs.join(', ')}`,
        3000
      );
      return;
    }

    try {
      // Get form values
      const userDetails = this.userDetailsForm.getRawValue();
      const entityDetails = this.entityDetailsForm.value;

      // Extract file paths from upload status
      // The key format should be: "assetId/filename" or just the key from response
      const getDocumentPath = (documentType: string): string => {
        const uploadStatus = this.uploadStatus[documentType];
        if (uploadStatus?.key) {
          // If key already contains the full path format, use it
          if (uploadStatus.key.includes('/')) {
            return uploadStatus.key;
          }
          // Otherwise, construct path from assetId and filename
          const fileName = this.uploadedFiles[documentType]?.name || '';
          if (uploadStatus.assetId && fileName) {
            return `${uploadStatus.assetId}/${uploadStatus.key}`;
          }
          return uploadStatus.key;
        }
        return '';
      };

      const entityTypeLabel =
        this.entityTypes.find(opt => opt.value === entityDetails.entityType)?.label ||
        entityDetails.entityType ||
        '';

      // Prepare payload according to API specification (backend expects website_link from the form)
      const payload = {
        name: entityDetails.entityName || '',
        org_sector: entityDetails.entitySector || '',
        website_link: (entityDetails.websiteUrl ?? '').toString().trim(),
        manager_email: userDetails.registeredEmail || '',
        emp_id: userDetails.employeeId || '',
        job_title: userDetails.jobTitle || '',
        entity_type: entityTypeLabel,
        address: entityDetails.address || '',
        relevant_doc_path: this.uploadStatus['others']?.success ? getDocumentPath('others') : ' ',
        letter_of_authorization: getDocumentPath('authorization'),
        phone_no: '9999999999', // Constant phone number
        logo_path: '', // Logo not in form, using empty string
        certificate_path: getDocumentPath('incorporation'),
        pancard_path: getDocumentPath('panCard'),
        organisation_documents: getDocumentPath('authorization'), // Constant: use certificate_path value
      };

      console.log('Submitting organisation request with payload:', payload);

      // Call the API
      const response = await this.authApiService.createOrganisationRequest(payload);
      
      console.log('Organisation request submitted successfully:', response);
      
      // Show success modal
      this.showSuccessModal = true;
      
    } catch (error: any) {
      console.error('Error submitting organisation request:', error);
      
      // Extract error message from API response
      let errorMessage = 'Failed to submit organisation request. Please try again.';
      
      if (error?.error?.detail) {
        errorMessage = error.error.detail;
      } else if (error?.detail) {
        errorMessage = error.detail;
      } else if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Show error toaster
      this.toasterService.error(errorMessage);
    }
  }

  onSuccessModalClose(): void {
    this.showSuccessModal = false;
  }

  async onSuccessModalConfirm(): Promise<void> {
    this.showSuccessModal = false;
    // Refresh sidebar to show "Awaiting Approval" notification
    if (this.sidebarComponent) {
      await this.sidebarComponent.refreshOrganisationStatus();
    }
    // Navigate to Organisation page
    this.router.navigate(['/consumer/data-provider-entity']);
  }

  markAllFormsAsTouched(): void {
    Object.values(this.userDetailsForm.controls).forEach(control => control.markAsTouched());
    Object.values(this.entityDetailsForm.controls).forEach(control => control.markAsTouched());
    Object.values(this.documentsForm.controls).forEach(control => control.markAsTouched());
  }

  onCancel(): void {
    this.router.navigate(['/consumer/join-organisation']);
  }

  // Entity Type Dropdown Methods
  toggleEntityTypeDropdown(): void {
    this.isEntityTypeDropdownOpen = !this.isEntityTypeDropdownOpen;
    if (this.isEntityTypeDropdownOpen) {
      this.isEntitySectorDropdownOpen = false;
    }
  }

  selectEntityType(option: EntityType): void {
    this.entityDetailsForm.patchValue({ entityType: option.value });
    this.isEntityTypeDropdownOpen = false;
    this.entityDetailsForm.get('entityType')?.markAsTouched();
  }

  getSelectedEntityTypeLabel(): string {
    const selectedValue = this.entityDetailsForm.get('entityType')?.value;
    if (!selectedValue) {
      return 'Select entity type';
    }
    const selectedOption = this.entityTypes.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select entity type';
  }

  isEntityTypeSelected(value: string): boolean {
    return this.entityDetailsForm.get('entityType')?.value === value;
  }

  clearEntityType(event: Event): void {
    event.stopPropagation();
    this.entityDetailsForm.patchValue({ entityType: '' });
    this.entityDetailsForm.get('entityType')?.markAsTouched();
  }

  // Entity Sector Dropdown Methods
  toggleEntitySectorDropdown(): void {
    this.isEntitySectorDropdownOpen = !this.isEntitySectorDropdownOpen;
    if (this.isEntitySectorDropdownOpen) {
      this.isEntityTypeDropdownOpen = false;
    }
  }

  selectEntitySector(option: EntitySector): void {
    this.entityDetailsForm.patchValue({ entitySector: option.value });
    this.isEntitySectorDropdownOpen = false;
    this.entityDetailsForm.get('entitySector')?.markAsTouched();
  }

  getSelectedEntitySectorLabel(): string {
    const selectedValue = this.entityDetailsForm.get('entitySector')?.value;
    if (!selectedValue) {
      return 'Select sector';
    }
    const selectedOption = this.entitySectors.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Select sector';
  }

  isEntitySectorSelected(value: string): boolean {
    return this.entityDetailsForm.get('entitySector')?.value === value;
  }

  clearEntitySector(event: Event): void {
    event.stopPropagation();
    this.entityDetailsForm.patchValue({ entitySector: '' });
    this.entityDetailsForm.get('entitySector')?.markAsTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef?.nativeElement?.contains(event.target)) {
      this.isEntityTypeDropdownOpen = false;
      this.isEntitySectorDropdownOpen = false;
    }
  }
}

